import {
  Search,
  Filter,
  ArrowUpDown,
  SortAsc,
  SortDesc,
  X,
  SlidersHorizontal,
} from 'lucide-react';
import { PartnershipTag, PARTNERSHIP_STATUSES } from '../../types/partnership';
import { cn } from '../../utils/cn';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useState } from 'react';

interface PartnershipFiltersProps {
  searchQuery: string;
  onSearchChange: (v: string) => void;
  statusFilter: string;
  onStatusChange: (v: string) => void;
  tagFilter: string;
  onTagChange: (v: string) => void;
  tags: PartnershipTag[];
  priorityMin: string;
  onPriorityMinChange: (v: string) => void;
  priorityMax: string;
  onPriorityMaxChange: (v: string) => void;
  sortBy: string;
  onSortByChange: (v: string) => void;
  sortOrder: 'asc' | 'desc';
  onSortOrderChange: (v: 'asc' | 'desc') => void;
}

export const PartnershipFilters = ({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusChange,
  tagFilter,
  onTagChange,
  tags,
  priorityMin,
  onPriorityMinChange,
  priorityMax,
  onPriorityMaxChange,
  sortBy,
  onSortByChange,
  sortOrder,
  onSortOrderChange,
}: PartnershipFiltersProps) => {
  const isMobile = useIsMobile();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const hasActiveFilters = statusFilter || tagFilter || priorityMin || priorityMax;

  const clearFilters = () => {
    onStatusChange('');
    onTagChange('');
    onPriorityMinChange('');
    onPriorityMaxChange('');
    onSearchChange('');
  };

  return (
    <div className="space-y-2">
      {/* Search + basic filters row */}
      <div className="flex flex-col md:flex-row md:items-center gap-2">
        {/* Search */}
        <div data-tour="pr-search" className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
          <input
            type="text"
            aria-label="Search partnerships"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="glass-input w-full pl-9"
            placeholder="Search partnerships, modpacks, creators…"
          />
        </div>

        {/* Status */}
        <div className="relative md:w-44">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
          <select
            value={statusFilter}
            onChange={(e) => onStatusChange(e.target.value)}
            className="glass-select w-full pl-9"
            aria-label="Filter by status"
          >
            <option value="">All Statuses</option>
            {PARTNERSHIP_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Sort */}
        <div className="relative md:w-44">
          <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
          <select
            value={sortBy}
            onChange={(e) => onSortByChange(e.target.value)}
            className="glass-select w-full pl-9"
            aria-label="Sort by"
          >
            <option value="created_at">Date Created</option>
            <option value="updated_at">Last Updated</option>
            <option value="priority">Priority</option>
            <option value="status">Status</option>
            <option value="start_date">Start Date</option>
          </select>
        </div>

        {/* Sort order + Advanced toggle */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="btn-icon"
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            aria-label={sortOrder === 'asc' ? 'Sort ascending' : 'Sort descending'}
          >
            {sortOrder === 'asc' ? (
              <SortAsc className="w-4 h-4" />
            ) : (
              <SortDesc className="w-4 h-4" />
            )}
          </button>
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={cn('btn-ghost btn-xs', showAdvanced && 'text-accent-300')}
            aria-expanded={showAdvanced}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            {showAdvanced ? 'Hide filters' : 'More filters'}
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="btn-ghost btn-xs text-red-400 hover:text-red-300"
            >
              <X className="w-3 h-3" />
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Advanced filters */}
      {showAdvanced && (
        <div className={cn('grid gap-3 subcard p-3', isMobile ? 'grid-cols-1' : 'grid-cols-3')}>
          {/* Tag filter */}
          <div>
            <label className="text-xs font-medium text-glass-400 mb-1.5 block">Tag</label>
            <select
              value={tagFilter}
              onChange={(e) => onTagChange(e.target.value)}
              className="glass-select w-full"
            >
              <option value="">All Tags</option>
              {tags.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </select>
          </div>

          {/* Priority range */}
          <div>
            <label className="text-xs font-medium text-glass-400 mb-1.5 block">Min Priority</label>
            <input
              type="number"
              min={1}
              max={10}
              value={priorityMin}
              onChange={(e) => onPriorityMinChange(e.target.value)}
              className="glass-input w-full"
              placeholder="1"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-glass-400 mb-1.5 block">Max Priority</label>
            <input
              type="number"
              min={1}
              max={10}
              value={priorityMax}
              onChange={(e) => onPriorityMaxChange(e.target.value)}
              className="glass-input w-full"
              placeholder="10"
            />
          </div>
        </div>
      )}
    </div>
  );
};
